import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Upload, Download } from "lucide-react";
import { toast } from "sonner";
import api, { download, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useApi, PageHeader, StatusBadge, Loading, Empty, Select, Pager, TableWrap } from "../components/common";
import LeadForm, { useDirectory } from "../components/LeadForm";
import { Button } from "../components/ui/button";
import { fdate, inr, todayIST } from "../lib/format";

export default function Leads() {
  const { user, settings, meta } = useAuth();
  const [flt, setFlt] = useState({ q: "", status: "", source: "", assigned_to: "", priority: "", followup: "", archived: false });
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(false);
  const fileRef = useRef();
  const sales = useDirectory("sales");
  const qs = new URLSearchParams({ ...Object.fromEntries(Object.entries(flt).filter(([, v]) => v !== "" && v !== false)), page, page_size: 25 }).toString();
  const { data, loading, reload } = useApi(`/leads?${qs}`);
  const set = (k) => (v) => { setFlt({ ...flt, [k]: v }); setPage(1); };
  const t = todayIST();

  const importCsv = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/leads/import", fd);
      toast.success(`Imported ${r.data.created} leads, skipped ${r.data.skipped_duplicates} duplicates`);
      if (r.data.errors.length) toast.warning(r.data.errors.slice(0, 3).join("\n"));
      reload();
    } catch (ex) { toast.error(errMsg(ex)); }
    e.target.value = "";
  };

  return (
    <div>
      <PageHeader title="Leads" subtitle={user.role === "sales" ? "Leads assigned to you" : "All leads across the sales team"} actions={<>
        <input type="file" accept=".csv" ref={fileRef} hidden onChange={importCsv} data-testid="lead-import-input" />
        <Button variant="outline" onClick={() => fileRef.current.click()} data-testid="lead-import-button" title="Columns: client_name, studio_name, phone, whatsapp, email, city, source, service, est_sheets, est_value, priority, notes"><Upload className="h-4 w-4 mr-1" />Import CSV</Button>
        <Button variant="outline" onClick={() => download("/leads/export", "leads.csv")} data-testid="lead-export-button"><Download className="h-4 w-4 mr-1" />Export</Button>
        <Button className="vmw-gradient" onClick={() => setOpen(true)} data-testid="lead-add-button"><Plus className="h-4 w-4 mr-1" />New Lead</Button>
      </>} />
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-2 mb-4">
        <input data-testid="lead-filter-search" className="vmw-input col-span-2" placeholder="Search name, studio, phone, ID…" value={flt.q} onChange={(e) => set("q")(e.target.value)} />
        <Select testid="lead-filter-status" placeholder="All stages" options={meta?.lead_stages || []} value={flt.status} onChange={set("status")} />
        <Select testid="lead-filter-source" placeholder="All sources" options={settings?.lead_sources || []} value={flt.source} onChange={set("source")} />
        <Select testid="lead-filter-priority" placeholder="Any priority" options={meta?.priorities || []} value={flt.priority} onChange={set("priority")} />
        <Select testid="lead-filter-followup" placeholder="Any follow-up" options={[{ value: "overdue", label: "Overdue follow-ups" }, { value: "today", label: "Due today" }]} value={flt.followup} onChange={set("followup")} />
        {user.role === "owner" ? <Select testid="lead-filter-assigned" placeholder="All salespeople" options={sales.map((s) => ({ value: s.id, label: s.name }))} value={flt.assigned_to} onChange={set("assigned_to")} />
          : <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={flt.archived} onChange={(e) => set("archived")(e.target.checked)} />Archived</label>}
      </div>
      <TableWrap>
        {loading && !data ? <Loading /> : !data?.items.length ? <Empty text="No leads match your filters" /> : (
          <table className="vmw-table" data-testid="leads-table">
            <thead><tr><th>Lead</th><th>Client / Studio</th><th>Phone</th><th>Source</th><th>Est. Value</th><th>Priority</th><th>Next Follow-up</th><th>Stage</th></tr></thead>
            <tbody>{data.items.map((l) => (
              <tr key={l.id} data-testid={`lead-row-${l.id}`}>
                <td><Link className="font-mono text-xs font-semibold text-primary" to={`/leads/${l.id}`}>{l.code}</Link><div className="text-[11px] text-muted-foreground">{fdate(l.created_date)}</div></td>
                <td><Link to={`/leads/${l.id}`} className="font-medium hover:text-primary">{l.client_name}</Link><div className="text-xs text-muted-foreground">{l.studio_name}</div></td>
                <td className="whitespace-nowrap">{l.phone}</td><td>{l.source}</td><td className="font-num">{inr(l.est_value)}</td>
                <td><StatusBadge value={l.priority} /></td>
                <td className={l.next_followup && l.next_followup < t ? "text-rose-600 font-semibold" : ""}>{fdate(l.next_followup)}</td>
                <td><StatusBadge value={l.status} /></td>
              </tr>))}</tbody>
          </table>
        )}
        {data && <Pager page={page} total={data.total} pageSize={25} onPage={setPage} />}
      </TableWrap>
      <LeadForm open={open} onClose={() => setOpen(false)} onSaved={reload} />
    </div>
  );
}
